import { Plus, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  type AgentDraft,
  emptyOttoConfig,
  type OttoConfigDraft,
} from "@/features/agents/components/AgentDraft";
import { cn } from "@/lib/utils";

// Variaveis disponiveis para os prompts (espelha
// src/services/otto/variables.py no backend).
const VARIABLES = [
  "messages_text",
  "funnel_state_name",
  "funnel_steps_allowed",
  "tag_options_allowed",
  "current_tags",
  "customer_name",
  "customer_phone",
  "customer_email",
  "customer_document",
  "channel_type",
  "channel_name",
  "conversation_id",
  "ordinal",
  "last_customer_message_at",
];

interface Props {
  draft: AgentDraft;
  onChange: (next: AgentDraft) => void;
}

export function OttoConfigTab({ draft, onChange }: Props) {
  const { t } = useTranslation("agents");
  const cfg = draft.otto_config ?? emptyOttoConfig();
  const isExternal = draft.agent_type === "external";

  function patch(next: Partial<OttoConfigDraft>) {
    onChange({ ...draft, otto_config: { ...cfg, ...next } });
  }

  return (
    <div className="space-y-6">
      {/* Toggle de habilitacao. */}
      <div className="space-y-2 rounded-md border p-4">
        <Label
          htmlFor="otto-enabled"
          className="flex items-center gap-2 font-medium"
        >
          <input
            id="otto-enabled"
            type="checkbox"
            checked={cfg.enabled}
            onChange={(e) => patch({ enabled: e.target.checked })}
          />
          {t("otto.enabledLabel")}
        </Label>
        <p className="text-xs text-muted-foreground">{t("otto.enabledHint")}</p>
      </div>

      {/* Agente externo: classificacao/follow-up sao respondidos pelo
          proprio bot remoto -- sem prompts locais para editar. */}
      {isExternal && cfg.enabled ? (
        <div className="rounded-md border border-dashed bg-muted/30 p-4">
          <p className="text-sm text-muted-foreground">
            {t("otto.externalHint")}
          </p>
        </div>
      ) : null}

      {cfg.enabled && !isExternal ? (
        <>
          <VariablesHelper />

          {/* Funil. */}
          <section className="space-y-3 rounded-md border p-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t("otto.funnelSection")}
            </h3>
            <StringListEditor
              label={t("otto.funnelStepsLabel")}
              hint={t("otto.funnelStepsHint")}
              placeholder={t("otto.funnelStepsPlaceholder")}
              values={cfg.funnel_steps}
              onChange={(funnel_steps) => patch({ funnel_steps })}
            />
            <PromptField
              id="otto-funnel-prompt"
              label={t("otto.funnelPromptLabel")}
              value={cfg.funnel_prompt_text}
              onChange={(v) => patch({ funnel_prompt_text: v })}
            />
          </section>

          {/* Tags. */}
          <section className="space-y-3 rounded-md border p-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t("otto.tagsSection")}
            </h3>
            <StringListEditor
              label={t("otto.tagOptionsLabel")}
              hint={t("otto.tagOptionsHint")}
              placeholder={t("otto.tagOptionsPlaceholder")}
              values={cfg.tag_options}
              onChange={(tag_options) => patch({ tag_options })}
            />
            <PromptField
              id="otto-tags-prompt"
              label={t("otto.tagsPromptLabel")}
              value={cfg.tags_prompt_text}
              onChange={(v) => patch({ tags_prompt_text: v })}
            />
          </section>

          {/* Follow-up. */}
          <section className="space-y-3 rounded-md border p-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t("otto.followupSection")}
            </h3>
            <PromptField
              id="otto-followup-prompt"
              label={t("otto.followupPromptLabel")}
              value={cfg.followup_prompt_text}
              onChange={(v) => patch({ followup_prompt_text: v })}
            />
          </section>
        </>
      ) : null}
    </div>
  );
}

function PromptField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={8}
        className="font-mono text-sm"
      />
    </div>
  );
}

function StringListEditor({
  label,
  hint,
  placeholder,
  values,
  onChange,
}: {
  label: string;
  hint: string;
  placeholder: string;
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const [text, setText] = useState("");

  function add() {
    const v = text.trim();
    if (!v || values.includes(v)) {
      setText("");
      return;
    }
    onChange([...values, v]);
    setText("");
  }

  function remove(value: string) {
    onChange(values.filter((v) => v !== value));
  }

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <p className="text-xs text-muted-foreground">{hint}</p>
      <div className="flex gap-2">
        <Input
          value={text}
          placeholder={placeholder}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="outline" onClick={add}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <div className={cn("flex flex-wrap gap-2", values.length === 0 && "hidden")}>
        {values.map((v) => (
          <Badge key={v} variant="secondary" className="gap-1">
            {v}
            <button
              type="button"
              onClick={() => remove(v)}
              className="ml-1 rounded-full hover:bg-muted"
              aria-label={`remover ${v}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

function VariablesHelper() {
  const { t } = useTranslation("agents");
  return (
    <div className="space-y-2 rounded-md border border-dashed bg-muted/30 p-3">
      <p className="text-xs font-medium text-muted-foreground">
        {t("otto.variablesHelper")}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {VARIABLES.map((v) => (
          <code
            key={v}
            className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs"
          >{`{{${v}}}`}</code>
        ))}
      </div>
    </div>
  );
}
